import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { ResumePackage } from "../../types/orders-managment/packages";

interface ResumePackagesResponse {
  data?: ResumePackage[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useResumePackages = () => {
  return useQuery({
    queryKey: ["ResumePackages"],
    queryFn: async (): Promise<ResumePackage[]> => {
      const response = await USERAXIOS.get<
        ResumePackagesResponse | ResumePackage[]
      >("/resume_packages");

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.data) {
        return response.data.data;
      }

      return [];
    },
    enabled: true,
  });
};

export const useResumePackagesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["ResumePackages", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<ResumePackagesResponse> => {
      const response = await USERAXIOS.get<ResumePackagesResponse>(
        "/resume_packages",
        {
          params: {
            page: pageParam,
            per_page: pageSize,
            search: searchTerm || undefined,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta) return undefined;
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const packages = useMemo(() => {
    return data?.pages.flatMap((page) => page.data || []) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: packages,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta?.last_page || 0,
    totalItems: data?.pages[0]?.meta?.total || 0,
  };
};
